import React, { useState } from 'react';
import { Search, Shield, CheckCircle, AlertTriangle, Clock, ChevronDown } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ServiceHeroForm from '../../components/ServiceHeroForm';
import RegistrationModal from '../../components/RegistrationModal';

const TrademarkClearancePage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const clearanceLevels = [
    { name: 'Basic Clearance', price: '$199', features: ['USPTO database search', 'State registrations (50 states)', 'Basic risk assessment', '24-hour turnaround', 'Email report'] },
    { name: 'Standard Clearance', price: '$399', features: ['All Basic features', 'Common law search', 'Domain availability', 'Social media handles', 'Attorney review', '48-hour turnaround'], popular: true },
    { name: 'Comprehensive Clearance', price: '$699', features: ['All Standard features', 'International databases', 'Business name registrations', 'Phonetic analysis', 'Visual similarity analysis', 'Detailed legal opinion', 'Strategy consultation'] }
  ];

  const faqs = [
    { question: 'What is the difference between a clearance search and a trademark search?', answer: 'A clearance search is more comprehensive than a basic trademark search. While a trademark search focuses primarily on registered marks, a clearance search includes common law marks, business names, domain names, social media handles, and provides a legal opinion on registration likelihood. It\'s the most thorough pre-filing investigation available.' },
    { question: 'When should I get a clearance search?', answer: 'Get a clearance search before making any significant investment in your brand—before ordering inventory, printing materials, launching websites, or starting marketing campaigns. Ideally, conduct the search during the naming process to avoid falling in love with a name you can\'t protect.' },
    { question: 'How is this different from the free USPTO search?', answer: 'The free USPTO database only shows exact matches of registered federal trademarks. Our clearance search includes state registrations, common law marks (unregistered but still protected), pending applications, phonetically similar marks, visually similar logos, domain names, and social media. Plus, you get expert legal analysis of risk levels.' },
    { question: 'What if the search finds conflicts?', answer: 'We provide a detailed risk assessment for each potential conflict, categorized as high, medium, or low risk. We also recommend strategies: modifying your mark, selecting different goods/services classes, pursuing coexistence agreements, or choosing an alternative mark. Our attorneys guide you through the best path forward.' },
    { question: 'Can I file my trademark if there are some conflicts?', answer: 'It depends on the nature and severity of the conflicts. Low-risk conflicts (different industries, different geographic areas, clear differences in appearance/sound) may not prevent registration. High-risk conflicts (same industry, similar appearance, confusing similarity) likely will. Our clearance report provides specific recommendations for your situation.' }
  ];

  return (
    <div>
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Trademark <span className="text-[#168356]">Clearance Searches</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                The most comprehensive trademark availability investigation. Know with confidence that your mark is safe to use before you invest.
              </p>
              <button onClick={() => openModal('registration')} className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg">
                Order Clearance Search
              </button>
            </div>
          </div>
          <ServiceHeroForm title="Start Your Trademark Clearance" buttonText="Start Clearance" />
        </div>
      </div>

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">What is a Trademark Clearance Search?</h2>
          </div>
          <div className="max-w-4xl mx-auto text-lg text-gray-700 leading-relaxed space-y-4">
            <p>
              A trademark clearance search is the gold standard of pre-filing investigations. It goes far beyond a simple database search to provide a comprehensive analysis of whether your proposed trademark is available for registration and safe to use in commerce.
            </p>
            <p>
              This search examines federal and state trademark registrations, common law marks (unregistered but still protected), domain names, social media handles, business name registrations, and phonetically similar marks. Most importantly, it includes a legal opinion from an experienced trademark attorney analyzing your likelihood of success and potential risks.
            </p>
            <p>
              Think of it as a full background check on your trademark. Before you invest thousands in branding, marketing, and product development, a clearance search ensures you're not building on someone else's legal foundation. It's the difference between hoping your mark is available and knowing it is.
            </p>
          </div>
        </div>
      </div>

      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Clearance Search Packages</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {clearanceLevels.map((level, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg ${level.popular ? 'ring-2 ring-[#168356] transform scale-105' : ''}`}>
                {level.popular && <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">Recommended</div>}
                <h3 className="text-2xl font-bold text-black mb-2">{level.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-6">{level.price}</div>
                <ul className="space-y-3 mb-6">
                  {level.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className={`w-full py-3 rounded-lg font-semibold ${level.popular ? 'bg-[#d29b00] text-white' : 'bg-gray-100 text-black'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100">
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && <div className="px-6 pb-4 text-gray-700 leading-relaxed">{faq.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default TrademarkClearancePage;